<template>
	<div
		class="portal-file-input"
		:class="{ 'is-dragging': isDragging }"
		@dragenter.prevent="isDragging = true"
		@dragover.prevent="isDragging = true"
		@dragleave.prevent="isDragging = false"
		@drop.prevent="handleDrop"
	>
		<label class="file-input-trigger">
			<portal-icon name="paperclip" :size="18"></portal-icon>
			<span>
				<strong>{{ label }}</strong>
				<small>{{ placeholder }}</small>
			</span>
			<input
				type="file"
				:multiple="multiple"
				:aria-label="label"
				@change="handleSelection"
			/>
		</label>

		<div class="selected-files" v-if="value.length">
			<span class="selected-file" v-for="(file, index) in value" :key="fileKey(file, index)">
				<span>{{ file.name }}</span>
				<button type="button" :aria-label="`${removeLabel}: ${file.name}`" @click="removeFile(index)">
					<portal-icon name="x" :size="13"></portal-icon>
				</button>
			</span>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'PortalFileInput',

		props: {
			value: { type: Array, required: false, default: () => [] },
			label: { type: String, required: true },
			placeholder: { type: String, required: true },
			removeLabel: { type: String, required: true },
			multiple: { type: Boolean, required: false, default: true },
		},

		data() {
			return {
				isDragging: false,
			};
		},

		methods: {
			handleSelection(event) {
				this.addFiles(event.target.files);
				event.target.value = '';
			},

			handleDrop(event) {
				this.isDragging = false;
				this.addFiles(event.dataTransfer.files);
			},

			addFiles(fileList) {
				const incoming = Array.from(fileList || []);
				if (!incoming.length) return;
				const files = this.multiple ? [...this.value, ...incoming] : incoming.slice(0, 1);
				this.$emit('input', files);
				this.$emit('change', files);
			},

			removeFile(index) {
				const files = [...this.value];
				files.splice(index, 1);
				this.$emit('input', files);
				this.$emit('change', files);
			},

			fileKey(file, index) {
				return `${file.name}-${file.size || 0}-${file.lastModified || 0}-${index}`;
			},
		},
	};
</script>

<style scoped lang="scss">
	.portal-file-input {
		min-width: 0;
		display: grid;
		gap: 9px;
	}

	.file-input-trigger {
		min-height: 58px;
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 9px 12px;
		border: 1px dashed color-mix(in srgb, var(--portal-muted) 48%, var(--portal-border));
		border-radius: var(--brand-field-radius, 12px);
		background: var(--v-gray_10-base);
		color: var(--portal-muted);
		cursor: pointer;
		transition: border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease;
	}

	.file-input-trigger:hover,
	.portal-file-input.is-dragging .file-input-trigger,
	.file-input-trigger:focus-within {
		border-color: var(--v-primary-base);
		background: color-mix(in srgb, var(--v-primary-base) 4%, var(--portal-surface));
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--v-primary-base) 10%, transparent);
	}

	.file-input-trigger > span {
		min-width: 0;
		display: grid;
		gap: 2px;
	}

	.file-input-trigger strong {
		color: var(--portal-text);
		font-size: 12px;
		font-weight: 680;
	}

	.file-input-trigger small {
		color: var(--portal-muted);
		font-size: 11px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	input {
		position: absolute;
		width: 1px;
		height: 1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
	}

	.selected-files {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.selected-file {
		max-width: 100%;
		min-height: 28px;
		display: inline-flex;
		align-items: center;
		gap: 5px;
		padding: 0 4px 0 9px;
		border-radius: 999px;
		background: var(--v-gray_20-base);
		color: var(--portal-text);
		font-size: 11px;
		font-weight: 600;
	}

	.selected-file > span {
		max-width: 240px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.selected-file button {
		width: 22px;
		height: 22px;
		display: grid;
		place-items: center;
		padding: 0;
		border: 0;
		border-radius: var(--brand-button-radius, 999px);
		background: transparent;
		color: var(--portal-muted);
		cursor: pointer;
	}

	.selected-file button:hover,
	.selected-file button:focus-visible {
		outline: 0;
		background: var(--portal-surface);
		color: var(--portal-text);
	}

	@media (prefers-reduced-motion: reduce) {
		.file-input-trigger {
			transition: none;
		}
	}
</style>
